// src/pages/ActivityPage.jsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Typography, Card, Stack, CircularProgress,
  TextField, InputAdornment, FormControl, InputLabel,
  Select, MenuItem, ToggleButtonGroup, ToggleButton,
  IconButton, Tooltip, LinearProgress, Button,
  Avatar, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search, Refresh, Map, ViewList, FiberManualRecord,
  FileDownload, MyLocation, Person,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { selectToken } from '../../store/authSlice';

import { STATUS_CONFIG }     from './constants';
import { getStatus, getCoords, exportToExcel } from './utils';
import { useActivity }       from './hooks/useActivity';
import { useMap }            from './hooks/useMap';
import { useSocket }         from './hooks/useSocket';
import UserCard              from './components/UserCard';
import UserModal             from './components/UserModal';
import ActivityCell          from './components/ActivityCell';
import MapSidePanel          from './components/MapSidePanel';

// ─────────────────────────────────────────────────────────────────────────────

export default function ActivityPage() {
  const token = useSelector(selectToken);

  // ── Data & filtering ──────────────────────────────────────────────────────
  const {
    rows, filtered, loading,
    date, setDate,
    search, setSearch,
    statusFilter, setStatusFilter,
    fetchActivity,
    tasks, setTasks, tasksLoading, fetchTasks,
    exporting, setExporting,
  } = useActivity(token);

  // ── Map & trail ───────────────────────────────────────────────────────────
  // Stable ref so useMap can attach click handlers at marker creation time
  const onMarkerClickRef = useRef(null);

  const {
    mapRef,
    mapInstance, leafletRef,
    markersRef,
    trailLayersRef, trailPolylineRef, lastTrailDotRef, activityMarkersRef,
    activeTrailRowRef, trailRef,
    leafletReady,
    activeTrailRow,
    panelVisible,
    trail, setTrail,
    trailLoading,
    loadTrail,
    clearTrail,
    setMarkersOpacity,
  } = useMap({ filtered, token, onMarkerClickRef });

  // ── Socket (live pings + task submissions) ────────────────────────────────
  const openEntryModalRef = useRef(null);

  const { socketRef, socketConnected } = useSocket({
    token,
    activeTrailRowRef,
    trailRef,
    setTrail,
    setTasks,
    mapInstance,
    leafletRef,
    trailPolylineRef,
    trailLayersRef,
    lastTrailDotRef,
    activityMarkersRef,
    openEntryModalRef,
  });

  // ── Modal state ───────────────────────────────────────────────────────────
  const [selectedRow,    setSelectedRow]    = useState(null);
  const [modalOpen,      setModalOpen]      = useState(false);
  const [view,           setView]           = useState('map');
  const [selectedEntry,  setSelectedEntry]  = useState(null);
  const [entryModalOpen, setEntryModalOpen] = useState(false);

  openEntryModalRef.current = (entry) => { setSelectedEntry(entry); setEntryModalOpen(true); };

  // Kept as a ref so useMap's closure always calls the latest version
  onMarkerClickRef.current = (row) => {
    setSelectedRow(row);
    loadTrail(row, socketRef, fetchTasks);
  };

  // ── Sync URL params ───────────────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (activeTrailRow) {
      const uid = activeTrailRow.author?._id ?? activeTrailRow.author?.id ?? activeTrailRow._id;
      params.set('userid', uid);
    } else {
      params.delete('userid');
    }
    params.set('date', date);
    window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
  }, [activeTrailRow, date]);

  // ── Invalidate map size when switching back to map view ───────────────────
  useEffect(() => {
    if (view === 'map' && mapInstance.current) {
      setTimeout(() => mapInstance.current.invalidateSize(), 50);
    }
  }, [view]);

  // ── Restore selection from URL after rows load ────────────────────────────
  const hasRestoredRef = useRef(false);
  useEffect(() => {
    if (hasRestoredRef.current || rows.length === 0 || !leafletReady) return;
    const params = new URLSearchParams(window.location.search);
    const userid = params.get('userid');
    if (!userid) return;
    const match = rows.find((r) => (r.author?._id ?? r.author?.id) === userid || r._id === userid);
    if (match) {
      hasRestoredRef.current = true;
      setSelectedRow(match);
      loadTrail(match, socketRef, fetchTasks);
      const coords = getCoords(match);
      if (coords && mapInstance.current) {
        mapInstance.current.flyTo([coords.lat, coords.lng], 14, { duration: 1 });
      }
    }
  }, [rows, leafletReady]);

  // ── Add task markers whenever tasks array is updated ──────────────────────
  const drawnTaskIdsRef = useRef(new Set());

  useEffect(() => {
    if (!mapInstance.current || !leafletRef.current || !activeTrailRow) return;
    const L = leafletRef.current;

    tasks.forEach((entry) => {
      const id = String(entry._id);
      if (drawnTaskIdsRef.current.has(id)) return;
      drawnTaskIdsRef.current.add(id);

      const loc = entry.formData?.location;
      if (!loc) return;
      const lat = parseFloat(loc.latitude ?? loc.lat);
      const lng = parseFloat(loc.longitude ?? loc.lng ?? loc.long);
      if (isNaN(lat) || isNaN(lng)) return;

      const taskTime  = entry.createdAt ? format(new Date(entry.createdAt), 'hh:mm a') : '—';
      const taskLabel = entry.taskLabel || entry.taskId || 'Task';
      const isLive    = !!entry._isLive;
      const syncTag   = entry.synced
        ? '<span style="font-size:9px;color:#0a9e74;background:#e6f9f4;border:1px solid #b3ead9;padding:1px 5px;border-radius:3px;font-weight:700;">Synced</span>'
        : '<span style="font-size:9px;color:#c06000;background:#fff4e0;border:1px solid #ffd599;padding:1px 5px;border-radius:3px;font-weight:700;">Offline</span>';

      const diamondColor    = isLive ? '#0a9e74' : '#2dccd2';
      const diamondColorAlt = '#1a8fd6';
      const diamondHtml = `<div style="width:28px;height:28px;display:flex;align-items:center;justify-content:center;"><div style="width:18px;height:18px;background:linear-gradient(135deg,${diamondColor},${diamondColorAlt});border:2.5px solid #fff;border-radius:4px;transform:rotate(45deg);box-shadow:0 2px 8px rgba(16,117,186,0.45),0 0 0 2px rgba(16,117,186,0.2);"></div></div>`;
      const icon = L.divIcon({ html: diamondHtml, className: '', iconSize: [28, 28], iconAnchor: [14, 14], popupAnchor: [0, -18] });

      // Build popup with any available thumbnail
      let thumbUrl = '';
      (function walk(obj) {
        if (!obj || typeof obj !== 'object' || thumbUrl) return;
        Object.values(obj).forEach((v) => {
          if (thumbUrl) return;
          if (Array.isArray(v) && v[0]?.url) { thumbUrl = v[0].url; return; }
          if (v?.url) { thumbUrl = v.url; return; }
          if (typeof v === 'object') walk(v);
        });
      })(entry.formData);

      const popupHtml =
        '<div style="padding:11px 13px;min-width:210px;max-width:260px;font-family:DM Sans,sans-serif;">' +
        '<div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">' +
        '<div style="width:26px;height:26px;border-radius:6px;background:linear-gradient(135deg,#2dccd2,#1a8fd6);flex-shrink:0;display:flex;align-items:center;justify-content:center;"><div style="width:10px;height:10px;background:#fff;border-radius:2px;transform:rotate(45deg);"></div></div>' +
        '<div style="flex:1;min-width:0;"><div style="font-weight:700;font-size:12px;color:#0f2137;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">' + taskLabel + '</div>' +
        '<div style="display:flex;align-items:center;gap:5px;margin-top:2px;"><span style="font-size:10px;color:#6b7f8e;font-family:monospace;">' + taskTime + '</span>' + syncTag + '</div></div></div>' +
        (thumbUrl ? '<a href="' + thumbUrl + '" target="_blank" style="display:block;width:100%;height:90px;border-radius:7px;overflow:hidden;border:1px solid rgba(16,117,186,0.2);margin-bottom:8px;"><img src="' + thumbUrl + '" style="width:100%;height:100%;object-fit:cover;" /></a>' : '') +
        '</div>';

      const taskMarker = L.marker([lat, lng], { icon }).addTo(mapInstance.current);
      taskMarker.bindPopup(popupHtml, { maxWidth: 280, closeButton: false, className: 'trail-popup' });
      taskMarker.on('mouseover', () => taskMarker.openPopup());
      taskMarker.on('mouseout',  () => taskMarker.closePopup());
      taskMarker.on('click', () => { taskMarker.closePopup(); openEntryModalRef.current?.(entry); });
      activityMarkersRef.current.push(taskMarker);

      if (trailPolylineRef.current) trailPolylineRef.current.addLatLng([lat, lng]);
    });
  }, [tasks, activeTrailRow]);

  // Reset drawn-task tracker when switching user
  useEffect(() => { drawnTaskIdsRef.current = new Set(); }, [activeTrailRow?._id]);

  // ── Handle trail close ────────────────────────────────────────────────────
  const handleClearTrail = useCallback(() => {
    clearTrail(socketRef, setTasks);
  }, [clearTrail]);

  // ── List view row click ───────────────────────────────────────────────────
  const handleRowClick = (row) => {
    setSelectedRow(row);
    setModalOpen(true);
    const coords = getCoords(row);
    if (coords && mapInstance.current) {
      mapInstance.current.flyTo([coords.lat, coords.lng], 15, { duration: 1.2 });
      markersRef.current[row._id]?.openPopup();
    }
    if (view !== 'map') setView('map');
  };

  // ── Excel export ──────────────────────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try { await exportToExcel(filtered.length ? filtered : rows, date); }
    catch (e) { console.error('Export failed', e); }
    setExporting(false);
  };

  // ── Summary counters ──────────────────────────────────────────────────────
  const checkedIn  = rows.filter((r) => getStatus(r) === 'CHECKIN').length;
  const onBreak    = rows.filter((r) => getStatus(r) === 'STARTBREAK').length;
  const checkedOut = rows.filter((r) => getStatus(r) === 'CHECKOUT').length;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ height: 'calc(94vh)', display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* ── Page header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f2137', mb: 0.25 }}>
            Field Activity
          </Typography>
          <Typography variant="body2" sx={{ color: '#4a6a85' }}>
            Live user locations and check-in status
          </Typography>
        </Box>

        {/* Summary badges */}
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {[
            { label: 'Checked In',  count: checkedIn,  color: '#0a9e74', bg: '#e6f9f4', border: '#b3ead9' },
            { label: 'On Break',    count: onBreak,    color: '#7b1fa2', bg: '#f5e6ff', border: '#d9b3f5' },
            { label: 'Checked Out', count: checkedOut, color: '#c06000', bg: '#fff4e0', border: '#ffd599' },
          ].map((s) => (
            <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75, borderRadius: '10px', background: s.bg, border: `1px solid ${s.border}` }}>
              <FiberManualRecord sx={{ fontSize: 8, color: s.color }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: s.color }}>
                {s.count} {s.label}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* ── Controls bar ── */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
        <TextField
          size="small" placeholder="Search name, username, role…"
          value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: '#4a6a85', fontSize: 18 }} /></InputAdornment> }}
        />

        <TextField
          size="small" type="date" label="Date" value={date}
          onChange={(e) => { setDate(e.target.value); hasRestoredRef.current = false; }}
          sx={{ width: 160 }} InputLabelProps={{ shrink: true }}
        />

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="CHECKIN">Checked In</MenuItem>
            <MenuItem value="STARTBREAK">On Break</MenuItem>
            <MenuItem value="ENDBREAK">Break Ended</MenuItem>
            <MenuItem value="CHECKOUT">Checked Out</MenuItem>
          </Select>
        </FormControl>

        <ToggleButtonGroup value={view} exclusive onChange={(_, v) => v && setView(v)} size="small">
          <ToggleButton value="map"  sx={{ px: 1.5 }}><Map      sx={{ fontSize: 18 }} /></ToggleButton>
          <ToggleButton value="list" sx={{ px: 1.5 }}><ViewList sx={{ fontSize: 18 }} /></ToggleButton>
        </ToggleButtonGroup>

        <Button
          variant="contained" size="small"
          startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <FileDownload />}
          onClick={handleExport}
          disabled={exporting || rows.length === 0}
          sx={{ whiteSpace: 'nowrap', px: 2 }}
        >
          {exporting ? 'Exporting…' : 'Export Excel'}
        </Button>

        <Tooltip title="Refresh">
          <IconButton
            size="small" onClick={fetchActivity} disabled={loading}
            sx={{ color: '#2dccd2', background: alpha('#2dccd2', 0.08), border: `1px solid ${alpha('#2dccd2', 0.2)}`, borderRadius: '10px', width: 36, height: 36 }}
          >
            {loading ? <CircularProgress size={16} sx={{ color: '#2dccd2' }} /> : <Refresh fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>

      {/* ══════════════════════════════ MAP VIEW ══════════════════════════════ */}
      <Box sx={{ flex: 1, display: view === 'map' ? 'flex' : 'none', gap: 2, minHeight: 0 }}>

        {/* Map container */}
        <Box sx={{
          flex: 1, borderRadius: '16px',
          border: '1px solid rgba(16,117,186,0.15)',
          boxShadow: '0 2px 12px rgba(15,33,55,0.07)',
          position: 'relative', minHeight: 0,
          display: 'flex', flexDirection: 'column',
          overflow: 'visible',
        }}>
          <Box ref={mapRef} sx={{ width: '100%', flex: 1, borderRadius: '16px', overflow: 'hidden' }} />

          {!leafletReady && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f4f8' }}>
              <CircularProgress sx={{ color: '#2dccd2' }} />
            </Box>
          )}

          {trailLoading && (
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1002, borderRadius: '16px 16px 0 0', overflow: 'hidden' }}>
              <LinearProgress sx={{ height: 3, borderRadius: 0, backgroundColor: 'rgba(16,117,186,0.12)', '& .MuiLinearProgress-bar': { backgroundColor: '#2dccd2' } }} />
            </Box>
          )}

          {/* Marker count + socket status badge */}
          <Box sx={{
            position: 'absolute', top: 12, left: 12, zIndex: 1000,
            background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(16,117,186,0.2)', borderRadius: '10px',
            px: 1.5, py: 0.75, boxShadow: '0 2px 8px rgba(15,33,55,0.1)',
            display: 'flex', alignItems: 'center', gap: 1.5,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <MyLocation sx={{ fontSize: 14, color: '#2dccd2' }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f2137' }}>
                {filtered.filter((r) => getCoords(r)).length} on map
              </Typography>
            </Box>
            <Tooltip title={socketConnected ? 'Live updates active' : 'Connecting to live updates...'}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{
                  width: 7, height: 7, borderRadius: '50%',
                  background: socketConnected ? '#22c55e' : '#f59e0b',
                  boxShadow: socketConnected ? '0 0 6px #22c55e' : '0 0 6px #f59e0b',
                  animation: socketConnected ? 'none' : 'blink 1.2s infinite',
                }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: socketConnected ? '#22c55e' : '#f59e0b' }}>
                  {socketConnected ? 'Live' : 'Connecting'}
                </Typography>
              </Box>
            </Tooltip>
          </Box>

          {/* Slide-in side panel */}
          {activeTrailRow && (
            <MapSidePanel
              row={activeTrailRow}
              trail={trail}
              trailLoading={trailLoading}
              onClose={handleClearTrail}
              visible={panelVisible}
              tasks={tasks}
              tasksLoading={tasksLoading}
              selectedEntry={selectedEntry}
              entryModalOpen={entryModalOpen}
              onOpenEntry={(e) => { setSelectedEntry(e); setEntryModalOpen(true); }}
              onCloseEntry={() => setEntryModalOpen(false)}
            />
          )}
        </Box>

        {/* Right-hand user list sidebar */}
        <Card sx={{ width: 290, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid rgba(16,117,186,0.08)' }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#4a6a85', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {filtered.length} Users
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                <CircularProgress sx={{ color: '#2dccd2' }} size={28} />
              </Box>
            ) : filtered.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Person sx={{ fontSize: 40, color: '#c8d4dc', mb: 1 }} />
                <Typography sx={{ color: '#6b7f8e', fontSize: '0.875rem' }}>No users found</Typography>
              </Box>
            ) : filtered.map((row) => (
              <UserCard
                key={row.id}
                row={row}
                onClick={() => {
                  setSelectedRow(row);
                  loadTrail(row, socketRef, fetchTasks);
                  const coords = getCoords(row);
                  if (coords && mapInstance.current) {
                    mapInstance.current.flyTo([coords.lat, coords.lng], 14, { duration: 1 });
                  }
                }}
                isSelected={selectedRow?.id === row.id}
                hasTrail={activeTrailRow?.id === row.id && trail.length > 0}
              />
            ))}
          </Box>
        </Card>
      </Box>

      {/* ══════════════════════════════ LIST VIEW ══════════════════════════════ */}
      {view === 'list' && (
        <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {['#', 'User', 'Role / Project', 'Status', 'Check In', 'Start Break', 'End Break', 'Check Out'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', whiteSpace: 'nowrap', py: 1.25 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 8 }).map((_, j) => (
                        <TableCell key={j}>
                          <Box sx={{ height: 16, borderRadius: 1, background: 'rgba(16,117,186,0.07)' }} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, color: '#6b7f8e' }}>
                      No users found for this date
                    </TableCell>
                  </TableRow>
                ) : filtered.map((row, idx) => {
                  const status    = getStatus(row);
                  const statusCfg = STATUS_CONFIG[status];
                  const author    = row.author || {};
                  const isSelected = selectedRow?._id === row._id;
                  return (
                    <TableRow
                      key={row._id} hover
                      onClick={() => handleRowClick(row)}
                      sx={{ cursor: 'pointer', background: isSelected ? alpha('#2dccd2', 0.06) : 'inherit', '&:hover': { background: alpha('#2dccd2', 0.04) } }}
                    >
                      <TableCell sx={{ color: '#9aacb8', fontSize: '0.78rem', width: 36 }}>{idx + 1}</TableCell>

                      <TableCell sx={{ minWidth: 180 }}>
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                          <Box sx={{ position: 'relative', flexShrink: 0 }}>
                            <Avatar src={author.photoURL} sx={{ width: 34, height: 34, fontSize: '0.8rem', border: `2px solid ${statusCfg.color}` }}>
                              {author.firstName?.[0]}
                            </Avatar>
                            <Box sx={{ position: 'absolute', bottom: 0, right: 0, width: 9, height: 9, borderRadius: '50%', background: statusCfg.color, border: '1.5px solid #fff' }} />
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: '0.83rem', fontWeight: 600, color: '#0f2137', lineHeight: 1.2 }}>
                              {author.firstName} {author.lastName}
                            </Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: '#4a6a85' }}>@{author.username}</Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ minWidth: 140 }}>
                        <Typography sx={{ fontSize: '0.8rem', color: '#0f2137', fontWeight: 500 }}>{author.role?.title || '—'}</Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: '#4a6a85' }}>{row.project?.title || author.project?.title || ''}</Typography>
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'inline-block', fontSize: '0.68rem', fontWeight: 700, color: statusCfg.color, background: statusCfg.bg, border: `1px solid ${statusCfg.border}`, px: 1, py: 0.35, borderRadius: '6px', whiteSpace: 'nowrap' }}>
                          {statusCfg.label}
                        </Box>
                      </TableCell>

                      <TableCell sx={{ minWidth: 130 }}><ActivityCell data={row.checkin}    color="#0a9e74" /></TableCell>
                      <TableCell sx={{ minWidth: 130 }}><ActivityCell data={row.startbreak} color="#7b1fa2" /></TableCell>
                      <TableCell sx={{ minWidth: 130 }}><ActivityCell data={row.endbreak}   color="#2dccd2" /></TableCell>
                      <TableCell sx={{ minWidth: 130 }}><ActivityCell data={row.checkout}   color="#c06000" /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ px: 2, py: 1, borderTop: '1px solid rgba(16,117,186,0.08)', flexShrink: 0 }}>
            <Typography sx={{ fontSize: '0.75rem', color: '#4a6a85' }}>
              Showing <strong>{filtered.length}</strong> of <strong>{rows.length}</strong> users · Click any row for full details
            </Typography>
          </Box>
        </Card>
      )}

      {/* Detail modal (list view) */}
      <UserModal row={selectedRow} open={modalOpen} onClose={() => setModalOpen(false)} />
    </Box>
  );
}